/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  AvatarSkeleton, Badge, Card, FontWeight, HStack, Icon, ICON_ALERT_CIRCLE_OUTLINE, ICON_CARD_ACCOUNT_DETAILS_OUTLINE, Message, OverriddenValue, Stack, Text, TextSkeleton,
  TypographyName,
} from '@doveit/bricks';
import { CardHeaderProps } from '@doveit/bricks/dist/typings/components/card/composition';
import { formatDate } from '@doveit/hammer';
import { Contact } from '../../../providers/api/dtos';
import * as styles from './ViewContactPreview.style';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useContact from '../../hooks/use-contact/useContact';
import ContactAvatar from '../../components/contact-avatar/ContactAvatar';
import { identityDocumentTypeLabels } from '../../../labels';
import { isEmpty } from '../../../utils/object/object';

interface ViewContactPreviewPropsBaseProps extends React.AriaAttributes, Pick<CardHeaderProps, 'title' | 'primaryActions' | 'secondaryActions' | 'color' | 'icon'> {
  oldContact?: Partial<Contact>,
  showIdentityDocument?: boolean,
  requiredFields?: Partial<Record<keyof Pick<Contact, 'name' | 'email' | 'phoneNumber' | 'fiscalCode' | 'residence' | 'birthday' | 'birthplace'>, boolean>>
}

interface WithContactId {
  contactId: NonNullable<Contact['id']>,
  contact?: never,
}

interface WithContact {
  contact: Contact,
  contactId?: never,
}

function renderMissingField(label: string, typographyName: TypographyName.HEADING_4 | TypographyName.BODY_SMALL = TypographyName.BODY_SMALL) {
  const TypographyComponent = typographyName === TypographyName.HEADING_4 ? Text.H4 : Text.BodySmall;
  const iconSize = typographyName === TypographyName.HEADING_4 ? 20 : 16;

  return (
    <HStack gap={75} wrap="nowrap">
      <Icon
        path={ICON_ALERT_CIRCLE_OUTLINE}
        size={iconSize}
        color="critical.default.low"
      />
      <TypographyComponent
        color="critical.default.low"
        lineHeight="1"
        as="div"
      >
        {label}
      </TypographyComponent>
    </HStack>
  );
}

export type ViewContactPreviewProps = ViewContactPreviewPropsBaseProps & (WithContactId | WithContact);

export const LOAD_CONTACT_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati del contatto.';

const ViewContactPreview: React.FC<ViewContactPreviewProps> = ({
  contactId,
  title = 'Contatto',
  primaryActions,
  secondaryActions,
  color,
  icon,
  requiredFields,
  oldContact,
  showIdentityDocument = false,
  contact: externalContact,
  'aria-label': ariaLabel = 'view-contact-preview',
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const { data: loadedContact, isLoading: isContactLoading, error: contactError } = useContact(contactId);
  const contact = externalContact || loadedContact;

  if (isContactLoading) {
    return (
      <Card>
        <Card.Content>
          <div
            aria-label={`${ariaLabel} (skeleton)`}
            css={styles.basic}
          >
            <div css={styles.info}>
              <AvatarSkeleton size={isMobile ? 'M' : 'L'} />
              <div css={styles.textInfo}>
                <div>
                  <TextSkeleton count={2} mainWordsInLine={1} width="12rem" />
                </div>
              </div>
            </div>
          </div>
        </Card.Content>
      </Card>
    );
  }

  if (contactError) {
    return (
      <Message
        type="critical"
        message={LOAD_CONTACT_DATA_ERROR_MESSAGE}
      />
    );
  }

  return contact && (
    <Card
      {...rest}
      aria-label={ariaLabel}
    >
      {primaryActions && (
        <Card.Header
          color={color}
          icon={icon}
          aria-label={`${ariaLabel} (header)`}
          title={title}
          primaryActions={primaryActions}
          secondaryActions={secondaryActions}
        />
      )}
      <Card.Content>
        <Stack gap={200}>
          <div css={styles.basic}>
            <div css={styles.info}>
              <ContactAvatar size={isMobile ? 'M' : 'L'} />
              <div css={styles.textInfo}>
                {(!requiredFields?.name || contact.name) && (
                  <OverriddenValue
                    newValue={contact.name || 'Nome non presente'}
                    oldValue={oldContact?.name}
                    renderNewValue={(v: string) => (
                      <Text.H4
                        aria-label={`${ariaLabel} (title)`}
                        lineHeight="1"
                        as="div"
                      >
                        {v}
                      </Text.H4>
                    )}
                  />
                )}

                {!contact.name && requiredFields?.name && (
                  renderMissingField('Nome', TypographyName.HEADING_4)
                )}

                {contact.phoneNumber && (
                  <OverriddenValue
                    newValue={contact.phoneNumber}
                    oldValue={oldContact?.phoneNumber}
                    renderNewValue={(v: string) => (
                      <Text.BodySmall
                        fontWeight={FontWeight.LIGHT}
                        lineHeight="1"
                        as="div"
                      >
                        {v}
                      </Text.BodySmall>
                    )}
                  />
                )}

                {!contact.phoneNumber && requiredFields?.phoneNumber && (
                  renderMissingField('Numero di telefono')
                )}

                {contact.email && (
                  <OverriddenValue
                    newValue={contact.email}
                    oldValue={oldContact?.email}
                    renderNewValue={(v: string) => (
                      <Text.BodySmall
                        fontWeight={FontWeight.LIGHT}
                        lineHeight="1"
                        as="div"
                      >
                        {v}
                      </Text.BodySmall>
                    )}
                  />
                )}

                {!contact.email && requiredFields?.email && (
                  renderMissingField('Email')
                )}
              </div>
            </div>

            {((contact.birthday || contact.birthplace) || (requiredFields?.birthday || requiredFields?.birthplace)) && (
              <div css={styles.details}>
                {contact.birthday && (
                  <OverriddenValue
                    newValue={formatDate(new Date(contact.birthday))}
                    oldValue={oldContact?.birthday ? formatDate(new Date(oldContact.birthday)) : undefined}
                    renderNewValue={(v: string) => (
                      <Text.BodySmall>
                        Data nascita: {v}
                      </Text.BodySmall>
                    )}
                  />
                )}
                {!contact.birthday && requiredFields?.birthday && (
                  renderMissingField('Data di nascita')
                )}
                {contact.birthplace && (
                  <OverriddenValue
                    newValue={contact.birthplace}
                    oldValue={oldContact?.birthplace}
                    renderNewValue={(v: string) => (
                      <Text.BodySmall>
                        Luogo nascita: {v}
                      </Text.BodySmall>
                    )}
                  />
                )}
                {!contact.birthplace && requiredFields?.birthplace && (
                  renderMissingField('Luogo di nascita')
                )}
              </div>
            )}

            {((contact.residence?.normalizedAddress || contact.fiscalCode) || (requiredFields?.residence || requiredFields?.fiscalCode)) && (
              <div css={styles.details}>
                {contact.residence?.normalizedAddress && (
                  <OverriddenValue
                    newValue={contact.residence.normalizedAddress}
                    oldValue={oldContact?.residence?.normalizedAddress}
                    renderNewValue={(v: string) => (
                      <Text.BodySmall>
                        {v}
                      </Text.BodySmall>
                    )}
                  />
                )}

                {!contact.residence?.normalizedAddress && requiredFields?.residence && (
                  renderMissingField('Indirizzo')
                )}

                {contact.fiscalCode && (
                  <OverriddenValue
                    newValue={contact.fiscalCode}
                    oldValue={oldContact?.fiscalCode}
                    renderNewValue={(v: string) => (
                      <Text.BodySmall>
                        {v}
                      </Text.BodySmall>
                    )}
                  />
                )}

                {!contact.fiscalCode && requiredFields?.fiscalCode && (
                  renderMissingField('Codice fiscale')
                )}
              </div>
            )}
          </div>

          {showIdentityDocument && contact.identityDocument && !isEmpty(contact.identityDocument) && (
            <Card aria-label="Informazioni documento d'identità">
              <Card.Content>
                <HStack gap={150}>
                  <Icon
                    path={ICON_CARD_ACCOUNT_DETAILS_OUTLINE}
                    size={24}
                  />

                  {contact.identityDocument.type && (
                    <Badge
                      label={identityDocumentTypeLabels[contact.identityDocument.type]}
                      size="XS"
                    />
                  )}

                  {contact.identityDocument.number && (
                    <Text.BodySmall>
                      {contact.identityDocument.number}
                    </Text.BodySmall>
                  )}

                  {contact.identityDocument.issuedBy && (
                    <Text.BodySmall>
                      {contact.identityDocument.issuedBy}
                    </Text.BodySmall>
                  )}

                  {contact.identityDocument.issuedAt && (
                    <Text.BodySmall>
                      {formatDate(new Date(contact.identityDocument.issuedAt), {
                        time: false,
                      })}
                    </Text.BodySmall>
                  )}
                </HStack>
              </Card.Content>
            </Card>
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default ViewContactPreview;
